import { Apple, Play, Smartphone } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import CivicPointsIconLogo from '../../../assets/icons/civicpoints_logo.svg';

const NewsDeepLinkPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const iosLink = 'https://apps.apple.com/vn/app/civicpoints/id6747657300';
    const androidLink = 'https://play.google.com/store/apps/details?id=com.idlemindtech.cityeg.prod&pcampaignid=web_share';

    const handleIOSDownload = () => {
        window.open(iosLink, '_blank');
    };

    const handleAndroidDownload = () => {
        window.open(androidLink, '_blank');
    };

    // Detect device type
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-[#0d2721] dark:via-[#1a1a1a] dark:to-[#0d2721] p-4">
            <div className="w-full max-w-md">
                <div className="bg-white dark:bg-[#242424] rounded-2xl shadow-xl p-8 md:p-10">
                    {/* Logo */}
                    <div className="flex justify-center mb-6">
                        <img
                            src={CivicPointsIconLogo}
                            alt="CivicPoints"
                            className="h-20 w-auto"
                        />
                    </div>

                    {/* Heading */}
                    <div className="text-center mb-8">
                        <h1 className="text-2xl md:text-3xl font-bold text-black dark:text-[#e5e5e5] mb-3">
                            Download CivicPoints
                        </h1>
                        <p className="text-base md:text-lg text-[#797979] dark:text-[#a0a0a0]">
                            Join community activities, earn points, and redeem rewards
                        </p>
                    </div>

                    {/* App Info Card */}
                    <div className="bg-[#eef9ef] dark:bg-[#0d2721] rounded-xl p-6 mb-8">
                        <div className="flex items-center gap-4">
                            <div className="bg-[#00C49A] rounded-xl p-4">
                                <Smartphone className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-lg font-bold text-[#004231] dark:text-[#00c49a] mb-1">
                                    CivicPoints App
                                </h2>
                                <p className="text-sm text-[#797979] dark:text-[#a0a0a0]">
                                    Your all-in-one platform for civic engagement
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Download Buttons */}
                    <div className="space-y-4">
                        {/* iOS Button */}
                        <button
                            onClick={handleIOSDownload}
                            className="
                w-full h-14 md:h-16
                bg-black hover:bg-gray-800 dark:bg-[#2a2a2a] dark:hover:bg-[#3a3a3a]
                text-white
                font-bold text-base md:text-lg
                rounded-[30px]
                focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 dark:focus:ring-gray-600
                transition-all duration-200
                transform hover:scale-[1.02] active:scale-[0.98]
                flex items-center justify-center gap-3
                shadow-lg hover:shadow-xl
              "
                        >
                            <Apple className="w-6 h-6" />
                            <span>Download for iOS</span>
                        </button>

                        {/* Android Button */}
                        <button
                            onClick={handleAndroidDownload}
                            className="
                w-full h-14 md:h-16
                bg-[#00C49A] hover:bg-[#00b38a] dark:hover:bg-[#00d4a8]
                text-[#004231] dark:text-[#004231]
                font-bold text-base md:text-lg
                rounded-[30px]
                focus:outline-none focus:ring-2 focus:ring-[#00c49a] focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-200
                transform hover:scale-[1.02] active:scale-[0.98]
                flex items-center justify-center gap-3
                shadow-lg hover:shadow-xl
              "
                        >
                            <Play className="w-6 h-6" />
                            <span>Download for Android</span>
                        </button>
                    </div>

                    {/* Features List */}
                    <div className="mt-8 pt-8 border-t border-[#dcdcdc] dark:border-[#404040]">
                        <h3 className="text-sm font-semibold text-[#75757d] dark:text-[#a0a0a0] mb-4 text-center">
                            Key Features
                        </h3>
                        <ul className="space-y-3 text-sm text-[#797979] dark:text-[#a0a0a0]">
                            <li className="flex items-start gap-2">
                                <span className="text-[#00C49A] mt-1">✓</span>
                                <span>City News & Alerts – Get real-time updates from your city</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#00C49A] mt-1">✓</span>
                                <span>Attend Local Events – Discover and attend community events</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#00C49A] mt-1">✓</span>
                                <span>Complete Surveys – Share your opinions on local issues</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#00C49A] mt-1">✓</span>
                                <span>Earn Rewards – Get points and redeem gift cards</span>
                            </li>
                        </ul>
                    </div>

                    {/* Device Detection Suggestion */}
                    {(isIOS || isAndroid) && (
                        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <p className="text-sm text-blue-800 dark:text-blue-300 text-center">
                                {isIOS
                                    ? 'Detected iOS device - Tap the iOS button to download'
                                    : 'Detected Android device - Tap the Android button to download'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-6 text-center">
                    <p className="text-xs text-[#75757d] dark:text-[#a0a0a0]">
                        News ID: {id || 'N/A'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NewsDeepLinkPage;
